import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, MapPin, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * <CitySelect /> — the canonical city picker used across CityDooh.
 *
 * ONE source of truth for typing / matching / display so vendor
 * profile, campaign wizard, Explore filter, and homepage hero all
 * behave identically. Same UX pattern as Zomato / Swiggy / Magicpin —
 * type-to-search combobox, Tier-1 metros pinned to the top, rest
 * alphabetical.
 *
 * Data comes from `GET /public/cities` — cached in-memory for the
 * lifetime of the tab so opening the picker on a second page is
 * instant. Cache is invalidated only on hard refresh; if Super Admin
 * adds a city the picker will pick it up on next page load which is
 * good enough — no aggressive revalidation needed for a table that
 * rarely changes.
 *
 * Props
 *   - value: string          — the currently-selected city NAME (canonical).
 *                              Empty string / null / undefined = no selection.
 *   - onChange: (name)=>void — called with the picked canonical name,
 *                              or "" if the user clears the selection.
 *   - placeholder: string    — button label when nothing is picked.
 *   - allowClear: bool       — show a "Clear selection" row at the bottom.
 *   - triggerClassName: str  — override styling on the trigger button.
 *   - disabled: bool
 *   - testIdPrefix: str      — every interactive element gets a
 *                              `${prefix}-*` data-testid for E2E.
 */

// Module-scoped cache so the picker feels instant on the second open.
// A Map would let us track staleness but the payload is <10KB and the
// endpoint is public+idempotent, so we go with a simple singleton.
let _citiesCache = null;
let _citiesPromise = null;

async function loadCitiesOnce() {
  if (_citiesCache) return _citiesCache;
  if (_citiesPromise) return _citiesPromise;
  _citiesPromise = api.publicCities()
    .then((r) => {
      _citiesCache = Array.isArray(r.data) ? r.data : [];
      return _citiesCache;
    })
    .catch(() => {
      // Fall back to an empty array so the picker doesn't crash the
      // parent page; caller sees "No cities available".
      _citiesCache = [];
      return _citiesCache;
    })
    .finally(() => {
      _citiesPromise = null;
    });
  return _citiesPromise;
}

/** Reset the cache — used by the Super Admin cities page after
 * adding/editing a city so the picker sees the update without a
 * hard refresh. */
export function invalidateCitiesCache() {
  _citiesCache = null;
  _citiesPromise = null;
}

export function CitySelect({
  value,
  onChange,
  placeholder = "Select city…",
  allowClear = true,
  triggerClassName,
  disabled = false,
  testIdPrefix = "city-select",
}) {
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState(_citiesCache);
  const [loading, setLoading] = useState(!_citiesCache);

  useEffect(() => {
    if (_citiesCache) return; // fast path
    let live = true;
    loadCitiesOnce().then((list) => {
      if (!live) return;
      setCities(list);
      setLoading(false);
    });
    return () => { live = false; };
  }, []);

  // Split majors from the rest — the server already sends them at the
  // top of the array (sort: is_major desc, name asc), so we just find
  // the boundary rather than re-sorting client-side.
  const { majors, others } = useMemo(() => {
    const list = cities || [];
    const m = list.filter((c) => c.is_major);
    const o = list.filter((c) => !c.is_major);
    return { majors: m, others: o };
  }, [cities]);

  const selectedLabel = (value || "").trim() || placeholder;

  const handleSelect = (name) => {
    onChange?.(name);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            triggerClassName,
          )}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <MapPin className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{selectedLabel}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(360px,calc(100vw-2rem))] p-0"
        align="start"
        data-testid={`${testIdPrefix}-popover`}
      >
        <Command
          filter={(candidate, search) => {
            // Custom filter: match on lowercased "name state" concat so
            // typing "Bang" hits "Bangalore" AND "Bangalore Karnataka".
            if (!search) return 1;
            return candidate.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Search city or state…"
            data-testid={`${testIdPrefix}-search`}
          />
          <CommandList>
            {loading ? (
              <div className="py-6 flex items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading cities…
              </div>
            ) : (
              <>
                <CommandEmpty>No cities match your search.</CommandEmpty>
                {majors.length > 0 && (
                  <CommandGroup heading="Popular cities">
                    {majors.map((c) => (
                      <CityRow
                        key={c.id}
                        city={c}
                        isSelected={value === c.name}
                        onSelect={handleSelect}
                        testId={`${testIdPrefix}-major-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    ))}
                  </CommandGroup>
                )}
                {others.length > 0 && (
                  <CommandGroup heading="All cities">
                    {others.map((c) => (
                      <CityRow
                        key={c.id}
                        city={c}
                        isSelected={value === c.name}
                        onSelect={handleSelect}
                        testId={`${testIdPrefix}-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                    ))}
                  </CommandGroup>
                )}
                {allowClear && value && (
                  <CommandGroup>
                    <CommandItem
                      value="__clear__"
                      onSelect={() => handleSelect("")}
                      className="text-muted-foreground"
                      data-testid={`${testIdPrefix}-clear`}
                    >
                      Clear selection
                    </CommandItem>
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CityRow({ city, isSelected, onSelect, testId }) {
  // The `value` prop is what cmdk uses for its internal filter; we join
  // name + state so searching by state ("Karnataka") also matches.
  return (
    <CommandItem
      value={`${city.name} ${city.state}`}
      onSelect={() => onSelect(city.name)}
      data-testid={testId}
    >
      <Check className={cn("mr-2 h-4 w-4", isSelected ? "opacity-100" : "opacity-0")} />
      <span className="flex-1 truncate">{city.name}</span>
      <span className="text-xs text-muted-foreground ml-2 shrink-0">{city.state}</span>
    </CommandItem>
  );
}

export default CitySelect;
